<script setup>
import { ref, computed } from 'vue';
import { onClickOutside } from '@vueuse/core';

// T-186: o seletor das telas da Base.
//
// O <select> nativo não servia por dois motivos, e os dois aparecem na tela. O tema desenha a
// seta com uma imagem de fundo que some no escuro, então o campo virava um retângulo sem sinal
// de que é clicável. E dentro de um <select> não dá para pintar o rótulo do filtro numa cor e a
// escolha em outra, que é justamente o que faz "Tipo: todos" ser legível de relance.
//
// Daí o gatilho e o painel desenhados aqui. `opcoes` é [{ valor, rotulo, contagem }]; a contagem
// é opcional e aparece em mono ao lado do rótulo.
const props = defineProps({
  modelValue: { type: [String, Number], default: '' },
  rotulo: { type: String, default: '' },
  opcoes: { type: Array, required: true },
  larguraCheia: { type: Boolean, default: false },
  placeholder: { type: String, default: '' },
});
const emit = defineEmits(['update:modelValue']);

const aberto = ref(false);
const raiz = ref(null);

onClickOutside(raiz, () => {
  aberto.value = false;
});

const escolhida = computed(() =>
  props.opcoes.find(opcao => opcao.valor === props.modelValue)
);
const textoEscolhido = computed(
  () => escolhida.value?.rotulo || props.placeholder
);

const escolher = opcao => {
  emit('update:modelValue', opcao.valor);
  aberto.value = false;
};
</script>

<template>
  <div
    ref="raiz"
    class="relative"
    :class="larguraCheia ? 'w-full min-w-0' : 'w-auto'"
    @keydown.esc="aberto = false"
  >
    <button
      type="button"
      class="h-[34px] inline-flex items-center gap-2 rounded-[5px] border border-solid bg-n-solid-2 pl-3 pr-2.5 text-[13px] text-left hover:bg-n-solid-3 focus:outline-none"
      :class="[
        larguraCheia ? 'w-full' : 'min-w-[130px]',
        aberto
          ? 'border-n-amber-6 ring-2 ring-n-amber-6/20'
          : 'border-n-strong hover:border-n-slate-6',
      ]"
      @click="aberto = !aberto"
    >
      <span v-if="rotulo" class="text-n-slate-10 flex-none">{{ rotulo }}</span>
      <span class="font-medium text-n-slate-12 truncate">
        {{ textoEscolhido }}
      </span>
      <span
        class="i-lucide-chevron-down size-3 text-n-slate-10 ml-auto flex-none transition-transform"
        :class="aberto ? 'rotate-180' : ''"
      />
    </button>

    <div
      v-if="aberto"
      class="absolute z-30 mt-1 min-w-full w-max max-w-[320px] rounded-md border border-solid border-n-strong bg-n-solid-1 p-1 shadow-lg"
    >
      <button
        v-for="opcao in opcoes"
        :key="String(opcao.valor)"
        type="button"
        class="w-full h-8 px-2.5 rounded flex items-center gap-2 text-[13px] hover:bg-n-alpha-2"
        :class="
          opcao.valor === modelValue
            ? 'text-n-amber-11 font-medium'
            : 'text-n-slate-11'
        "
        @click="escolher(opcao)"
      >
        <span class="truncate">{{ opcao.rotulo }}</span>
        <span
          v-if="opcao.contagem !== undefined && opcao.contagem !== null"
          class="font-mono text-[11px] text-n-slate-10 flex-none"
        >
          {{ opcao.contagem }}
        </span>
        <span
          v-if="opcao.valor === modelValue"
          class="i-lucide-check size-3.5 ml-auto flex-none"
        />
      </button>
    </div>
  </div>
</template>
